import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CreditCard, Check, Globe, Server, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalCheckout() {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [build, setBuild] = useState(null);
  const [includeHosting, setIncludeHosting] = useState(false);
  const [reviewOptIn, setReviewOptIn] = useState(true);
  const [promoCode, setPromoCode] = useState("");

  const cancelled = searchParams.get("cancelled") === "true";

  useEffect(() => {
    if (cancelled) {
      toast.error("Checkout was cancelled. You can try again when ready.");
    }
    fetchBuild();
  }, [leadId, cancelled]);

  const fetchBuild = async () => {
    try {
      const response = await axios.get(`${API}/portal/build/${leadId}`, {
        withCredentials: true
      });
      setBuild(response.data);
      
      // If already paid, redirect to download
      if (response.data.order?.status === "paid") {
        navigate(`/portal/download/${leadId}`);
      }
    } catch (error) {
      console.error("Failed to fetch build:", error);
      if (error.response?.status === 401) {
        navigate("/");
      } else {
        toast.error("Failed to load build details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/portal/checkout/${leadId}`,
        {
          include_hosting: includeHosting,
          review_opt_in: reviewOptIn,
          promo_code: promoCode.trim() || undefined
        },
        { withCredentials: true }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout failed:", error);
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  const websitePrice = 600;
  const hostingPrice = 15;
  const total = websitePrice + (includeHosting ? hostingPrice : 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
          <Button variant="ghost" onClick={() => navigate(`/portal/preview/${leadId}`)}>
            Back to Preview
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Left: Order Summary */}
          <div className="md:col-span-3 space-y-6">
            <div>
              <h1 className="text-2xl font-bold">Complete Your Order</h1>
              <p className="text-muted-foreground mt-1">
                Website for {build?.lead?.business_name}
              </p>
            </div>

            {/* Website Package */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe size={20} />
                  Website Package
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">Complete Multi-Page Website</p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>✓ Professional homepage</li>
                      <li>✓ Service pages</li>
                      <li>✓ Service area pages</li>
                      <li>✓ About & Contact pages</li>
                      <li>✓ Mobile-responsive design</li>
                      <li>✓ SEO-optimized content</li>
                    </ul>
                  </div>
                  <p className="text-2xl font-bold">${websitePrice}</p>
                </div>
              </CardContent>
            </Card>

            {/* Hosting Add-on */}
            <Card className={includeHosting ? "ring-2 ring-blue-500" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server size={20} />
                  Optional: Managed Hosting
                </CardTitle>
                <CardDescription>
                  Let us host and maintain your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <Checkbox 
                    id="hosting"
                    checked={includeHosting}
                    onCheckedChange={setIncludeHosting}
                    className="mt-1"
                  />
                  <Label htmlFor="hosting" className="flex-1 cursor-pointer">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium">Add Managed Hosting</p>
                        <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                          <li>• We deploy and host your site</li>
                          <li>• SSL certificate included</li>
                          <li>• Ongoing updates and support</li>
                        </ul>
                      </div>
                      <p className="text-lg font-semibold">${hostingPrice}/mo</p>
                    </div>
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Review Opt-in */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Checkbox 
                    id="review"
                    checked={reviewOptIn}
                    onCheckedChange={setReviewOptIn}
                    className="mt-1"
                    data-testid="review-optin-checkbox"
                  />
                  <Label htmlFor="review" className="cursor-pointer">
                    <p className="font-medium">Send me a follow-up email to request a review</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      We will send a friendly email 24 hours after purchase asking for your feedback. 
                      You can unsubscribe at any time.
                    </p>
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Promo Code */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag size={18} />
                  Promo Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Promo code will be applied at checkout
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right: Price Summary */}
          <div className="md:col-span-2">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Website Package</span>
                    <span>${websitePrice}.00</span>
                  </div>
                  {includeHosting && (
                    <div className="flex justify-between text-blue-600">
                      <span>Managed Hosting</span>
                      <span>${hostingPrice}.00/mo</span>
                    </div>
                  )}
                  {promoCode && (
                    <div className="flex justify-between text-green-600 text-sm">
                      <span>Promo: {promoCode}</span>
                      <span>Applied at checkout</span>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Due Today</span>
                  <span>${total}.00{includeHosting ? "*" : ""}</span>
                </div>
                
                {includeHosting && (
                  <p className="text-xs text-muted-foreground">
                    *Plus ${hostingPrice}/mo recurring for hosting
                  </p>
                )}

                <Button 
                  className="w-full h-12 text-lg"
                  onClick={handleCheckout}
                  disabled={processing}
                  data-testid="portal-pay-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2" size={20} />
                      Pay ${total}
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Secure payment powered by Stripe
                </p>

                <Separator />

                <div className="text-sm text-muted-foreground space-y-2">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-600" />
                    <span>7-day download access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-600" />
                    <span>You own your files</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-600" />
                    <span>No hidden fees</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
